import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'default' | 'danger';
  /** When true, shows a spinner on the confirm button and disables both buttons */
  loading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
  loading = false,
}) => {
  if (!open) return null;

  const isDanger = variant === 'danger';

  const iconBg = isDanger
    ? 'linear-gradient(135deg, #fee2e2, #fecaca)'
    : 'linear-gradient(135deg, #dbeafe, #bfdbfe)';
  const iconColor = isDanger ? '#dc2626' : '#2563eb';
  const iconBorder = isDanger ? '#fca5a5' : '#93c5fd';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        {/* Title Bar — compact */}
        <div className="modal-header" style={{ padding: '0.75rem 1rem', minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
            {isDanger
              ? <IconSystem.alert size={16} style={{ color: iconColor }} />
              : <IconSystem.info size={16} style={{ color: iconColor }} />
            }
            {title}
          </h2>
          <button className="modal-close" onClick={onCancel} disabled={loading} style={{ width: '1.625rem', height: '1.625rem', ...(loading ? { opacity: 0.3, pointerEvents: 'none' as const } : {}) }}>
            <IconSystem.x size={16} />
          </button>
        </div>

        <div className="modal-content" style={{ textAlign: 'center', padding: '1rem 1.5rem' }}>
          {/* Icon */}
          <div style={{
            width: '3.25rem',
            height: '3.25rem',
            borderRadius: '50%',
            background: iconBg,
            border: `2px solid ${iconBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            boxShadow: `0 4px 12px ${isDanger ? 'rgba(220, 38, 38, 0.12)' : 'rgba(37, 99, 235, 0.12)'}`,
          }}>
            {isDanger
              ? <IconSystem.alert size={22} style={{ color: iconColor }} />
              : <IconSystem.info size={22} style={{ color: iconColor }} />
            }
          </div>

          {/* Message */}
          <p style={{
            fontSize: '0.85rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            margin: 0,
          }}>
            {message}
          </p>
        </div>

        {/* Footer — compact */}
        <div className="modal-footer" style={{ justifyContent: 'center', gap: '0.75rem', padding: '0.75rem 1.5rem', minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={loading} style={{ minWidth: '100px', height: '2.125rem', fontSize: '0.8rem', ...(loading ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
            {cancelLabel}
          </button>
          <button
            className={isDanger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={loading}
            style={{ minWidth: '100px', height: '2.125rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem', ...(loading ? { opacity: 0.7, cursor: 'wait' } : {}) }}
          >
            {loading ? (
              <>
                <span style={{
                  display: 'inline-block', width: '14px', height: '14px',
                  border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                  borderRadius: '50%', animation: 'confirmDialogSpin 0.6s linear infinite',
                }} />
                Processing...
              </>
            ) : (
              <>
                {isDanger ? <IconSystem.trash size={14} /> : <IconSystem.check size={14} />}
                {confirmLabel}
              </>
            )}
          </button>
        </div>

        {/* Spinner keyframes */}
        {loading && (
          <style>{`@keyframes confirmDialogSpin { to { transform: rotate(360deg); } }`}</style>
        )}
      </div>
    </>
  );
};

export default ConfirmDialog;
